import React, { useState, useEffect } from 'react';
import { getAssignments, getMySubmissions } from '../../utils/api/AssignmentsApi';
import { hasInstructorPermissions } from '../../utils/permissions';
import AssignmentPreview from './AssignmentPreview';

const AssignmentList = ({ classContext, userRole, onSelectAssignment, onCreateNew, onNavigate, onViewGrade }) => {
  const isInstructor = hasInstructorPermissions(userRole);

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(isInstructor ? 'all' : 'published');
  const [previewAssignment, setPreviewAssignment] = useState(null);
  const [studentSubmissions, setStudentSubmissions] = useState({});

  useEffect(() => {
    if (classContext) {
      loadAssignments();
    }
  }, [filter, classContext]);

  const loadAssignments = async () => {
    if (!classContext) {
      setError('No class selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const filters = {};

      // Students only see published assignments
      if (!isInstructor) {
        filters.published = 'true';
      } else {
        // Instructors can filter
        if (filter === 'published') filters.published = 'true';
        if (filter === 'draft') filters.published = 'false';
      }

      const data = await getAssignments(filters, classContext);
      setAssignments(data.assignments || []);

      // For students, fetch their submissions to show status badges
      if (!isInstructor) {
        try {
          const subsResult = await getMySubmissions(classContext);
          const subsMap = {};
          (subsResult.submissions || []).forEach(sub => {
            subsMap[sub.assignment_id] = sub;
          });
          setStudentSubmissions(subsMap);
        } catch (subErr) {
          console.error('Error loading student submissions:', subErr);
        }
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      erd: 'ERD',
      code: 'Code',
      document: 'Document'
    };
    return labels[type] || type.toUpperCase();
  };

  const getComponentIcon = (type) => {
    switch(type) {
      case 'document': return 'DOC';
      case 'erd': return 'ERD';
      case 'code': return 'CODE';
      default: return 'FILE';
    }
  };

  const getTimeRemaining = (dueDate) => {
    if (!dueDate) return { text: 'No due date', percentage: 0, isOverdue: false };

    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;

    if (diff < 0) {
      const overdueDays = Math.floor(Math.abs(diff) / (1000 * 60 * 60 * 24));
      return {
        text: overdueDays > 0 ? `${overdueDays}d overdue` : 'Overdue',
        percentage: 100,
        isOverdue: true
      };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    let text;
    if (days > 7) {
      text = `${days}d remaining`;
    } else if (days > 0) {
      text = `${days}d ${hours}h`;
    } else if (hours > 0) {
      text = `${hours}h ${minutes}m`;
    } else {
      text = `${minutes}m`;
    }

    // Calculate percentage for progress bar (assuming 14 days is 100%)
    const totalTime = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
    const elapsed = totalTime - diff;
    const percentage = Math.min(100, Math.max(0, (elapsed / totalTime) * 100));

    return { text, percentage, isOverdue: false };
  };


  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading assignments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-red-600 text-sm text-center">
          Error loading assignments: {error}
        </div>
        <button
          onClick={loadAssignments}
          className="mt-3 px-5 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors duration-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 rounded-xl overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 flex-shrink-0 bg-white">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-2xl font-bold text-gray-900 m-0">
            {isInstructor ? 'Manage Assignments' : 'My Assignments'}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Only show filters to instructors */}
            {isInstructor && (
              <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                <button
                  className={`px-4 py-2 border-none text-sm font-medium cursor-pointer rounded-lg transition-all duration-200 ${
                    filter === 'all' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  className={`px-4 py-2 border-none text-sm font-medium cursor-pointer rounded-lg transition-all duration-200 ${
                    filter === 'published' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setFilter('published')}
                >
                  Published
                </button>
                <button
                  className={`px-4 py-2 border-none text-sm font-medium cursor-pointer rounded-lg transition-all duration-200 ${
                    filter === 'draft' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setFilter('draft')}
                >
                  Drafts
                </button>
              </div>
            )}

            {/* Only instructors can create assignments */}
            {isInstructor && (
              <button
                className="px-5 py-2.5 bg-blue-600 text-white border-none rounded-xl text-sm font-semibold cursor-pointer transition-all duration-200 shadow-sm hover:bg-blue-700 hover:shadow-md"
                onClick={onCreateNew}
              >
                + New Assignment
              </button>
            )}
          </div>
        </div>
      </div>

      {assignments.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-gray-100 max-w-md">
            <p className="text-gray-500 text-base m-0 mb-4">
              {isInstructor ? 'No assignments found.' : 'No assignments available yet.'}
            </p>
            {isInstructor && (
              <button
                className="px-6 py-2.5 bg-blue-600 text-white border-none rounded-xl text-sm font-semibold cursor-pointer hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
                onClick={onCreateNew}
              >
                Create your first assignment
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {assignments.map((assignment) => {
              const timeRemaining = getTimeRemaining(assignment.due_date);
              const hasMultipleComponents = assignment.components && assignment.components.length > 0;

              return (
                <div
                  key={assignment.assignment_id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-200"
                  onClick={() => {
                    if (isInstructor) {
                      onSelectAssignment && onSelectAssignment(assignment);
                    } else {
                      setPreviewAssignment(assignment);
                    }
                  }}
                >
                  <div className="p-5">
                    {/* Title and Status */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-base font-semibold text-gray-900 m-0 truncate">{assignment.title}</h3>
                          {isInstructor && (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${
                              assignment.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {assignment.is_published ? 'Published' : 'Draft'}
                            </span>
                          )}
                          {!isInstructor && (() => {
                            const sub = studentSubmissions[assignment.assignment_id];
                            if (!sub) return (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                                Not Started
                              </span>
                            );
                            if (sub.status === 'returned' || sub.status === 'graded') return (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                Graded {sub.score != null ? `- ${sub.score}%` : ''}
                              </span>
                            );
                            if (sub.status === 'submitted') return (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                Submitted
                              </span>
                            );
                            return (
                              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                                In Progress
                              </span>
                            );
                          })()}
                        </div>

                        {/* Component Icons for Multi-Component Assignments */}
                        {hasMultipleComponents && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-gray-500 font-medium">Components:</span>
                            {assignment.components.sort((a, b) => a.order - b.order).map((comp, idx) => {
                              const compKey = comp.component_id || `comp-${idx}`;
                              return (
                                <div key={compKey} className="flex items-center gap-1 bg-gray-50 px-2.5 py-1 rounded-lg text-xs border border-gray-100">
                                  <span className="font-semibold text-gray-700">{getComponentIcon(comp.type)}</span>
                                  <span className="text-gray-700 capitalize font-medium">{comp.type}</span>
                                  <span className="text-gray-500">({comp.points}pts)</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Time remaining indicator */}
                      <div className="flex items-center gap-3 min-w-[280px]">
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              timeRemaining.isOverdue ? 'bg-red-500' : 'bg-blue-600'
                            }`}
                            style={{ width: `${timeRemaining.percentage}%` }}
                          />
                        </div>
                        <span className={`text-sm font-semibold whitespace-nowrap ${
                          timeRemaining.isOverdue ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {timeRemaining.text}
                        </span>
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-4 flex-wrap">
                      {!hasMultipleComponents && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg">
                          <span className="text-xs text-blue-600 font-medium">Type:</span>
                          <span className="text-blue-800 text-xs font-semibold uppercase">
                            {getTypeLabel(assignment.assignment_type)}
                          </span>
                        </div>
                      )}
                      {assignment.environment_id && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 rounded-lg">
                          <span className="text-xs text-purple-600 font-medium">Env:</span>
                          <span className="text-purple-800 text-xs font-semibold">{assignment.environment_id}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-500 font-medium">Points:</span>
                        <span className="text-gray-900 text-xs font-semibold">{assignment.total_points}</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-500 font-medium">Due:</span>
                        <span className="text-gray-900 text-xs font-semibold">{formatDate(assignment.due_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Student Assignment Preview Sidebar */}
      {!isInstructor && previewAssignment && (
        <AssignmentPreview
          assignment={previewAssignment}
          submission={studentSubmissions[previewAssignment.assignment_id]}
          onClose={() => setPreviewAssignment(null)}
          onWorkOnAssignment={(assignment) => {
            setPreviewAssignment(null);
            onSelectAssignment && onSelectAssignment(assignment);
          }}
          onViewGrade={(assignment) => {
            setPreviewAssignment(null);
            onViewGrade && onViewGrade(assignment);
          }}
          onOpenComponent={(component, assignment) => {
            setPreviewAssignment(null);
            if (!onNavigate) return;
            const navData = {
              assignmentId: assignment.assignment_id,
              componentId: component.component_id
            };
            switch (component.type) {
              case 'erd':
                navData.environmentId = component.content_ref?.requirement_id;
                onNavigate('editor', navData);
                break;
              case 'code':
                navData.projectId = component.content_ref?.project_id;
                navData.fileId = component.content_ref?.file_id;
                onNavigate('code-editor', navData);
                break;
              default:
                navData.documentId = component.content_ref?.document_id;
                onNavigate('document-editor', navData);
            }
          }}
        />
      )}
    </div>
  );
};

export default AssignmentList;
